import moment from "moment";

// Fecha de hoy según el reloj local del navegador, normalizada a UTC sin hora
// (evita que moment.utc() sin argumentos devuelva el día siguiente cuando el
// offset local ya cruzó la medianoche UTC, ej. después de las 19:00 en Perú)
const getTodayUtc = () => moment.utc(moment().format("YYYY-MM-DD"));

// Constantes de configuración de estados de asistencia
export const STATUS_CONFIG = {
  presente: { icon: "check-circle", color: "success", text: "Presente" },
  tardanza: { icon: "clock", color: "warning", text: "Tardanza" },
  ausente: { icon: "times-circle", color: "danger", text: "Ausente" },
  justificado: { icon: "exclamation-circle", color: "info", text: "Justificado" },
};

export const DEFAULT_STATUS = { 
  icon: "question-circle", 
  color: "secondary", 
  text: "Desconocido" 
};

/**
 * Obtiene la configuración de un estado de asistencia
 * @param {string} status - Estado de asistencia
 * @returns {Object} Configuración del estado
 */
export const getStatusConfig = (status) => {
  return STATUS_CONFIG[status] || DEFAULT_STATUS;
};

/**
 * Calcula estadísticas de asistencia
 * @param {Object} attendanceData - Datos de asistencia
 * @returns {Object} Estadísticas calculadas
 */
export const calculateAttendanceStats = (attendanceData) => {
  if (!attendanceData?.statistics) {
    return {
      total: 0,
      present: 0,
      late: 0,
      absent: 0,
      justified: 0,
      percentage: "0%",
    };
  }

  const { statistics } = attendanceData.statistics;
  return {
    total: attendanceData.statistics.totalDays || 0,
    present: statistics.presente || 0,
    late: statistics.tardanza || 0,
    absent: (statistics.falta || 0) + (statistics.ausente || 0),
    justified: (statistics.justificada || 0) + (statistics.justificado || 0),
    percentage: attendanceData.statistics.attendancePercentage || "0%",
  };
};

/**
 * Extrae información del estudiante de los datos de asistencia
 * @param {Object} attendanceData - Datos de asistencia
 * @param {Object} fallbackStudent - Estudiante de respaldo
 * @returns {Object} Información del estudiante
 */
export const getStudentInfo = (attendanceData, fallbackStudent) => {
  if (attendanceData?.studentInfo?.student?.User) {
    return {
      student: attendanceData.studentInfo.student.User,
      cycle: attendanceData.studentInfo.academicCycle,
    };
  } else if (attendanceData?.attendanceRecords?.[0]?.Student?.User) {
    return {
      student: attendanceData.attendanceRecords[0].Student.User,
      cycle: attendanceData.attendanceRecords[0].Student.AcademicCycle,
    };
  } else {
    return {
      student: fallbackStudent.User,
      cycle: null,
    };
  }
};

/**
 * Obtiene el rango del ciclo académico
 * @param {Object} academicCycle - Ciclo académico
 * @returns {Object} Rango de fechas del ciclo
 */
export const getCycleRange = (academicCycle) => {
  if (academicCycle) {
    return {
      startDate: moment.utc(academicCycle.startDate),
      endDate: moment.utc(academicCycle.endDate),
    };
  }
  return {
    startDate: getTodayUtc().subtract(1, "year"),
    endDate: getTodayUtc().add(1, "year"),
  };
};

/**
 * Crea un mapa de asistencia para el mes seleccionado
 * @param {Array} attendanceRecords - Registros de asistencia
 * @param {string} selectedMonth - Mes en formato YYYY-MM
 * @returns {Object} Mapa de asistencia por fecha
 */
export const createMonthlyAttendanceMap = (attendanceRecords, selectedMonth) => {
  if (!attendanceRecords) return {};

  const attendanceMap = {};
  attendanceRecords.forEach((record) => {
    const date = moment.utc(record.attendanceDate);
    if (date.format("YYYY-MM") === selectedMonth) {
      attendanceMap[date.format("YYYY-MM-DD")] = record;
    }
  });

  return attendanceMap;
};

/**
 * Genera las opciones para el dropdown de meses
 * @param {Object} cycleRange - Rango del ciclo académico
 * @returns {Array} Array de opciones para el dropdown
 */
export const generateMonthOptions = (cycleRange) => {
  const months = [];
  let currentDate = cycleRange.startDate.clone().startOf("month");
  const endDate = cycleRange.endDate.clone().endOf("month");

  while (currentDate.isSameOrBefore(endDate, "month")) {
    months.push({
      key: currentDate.format("YYYY-MM"),
      text:
        currentDate.format("MMMM YYYY").charAt(0).toUpperCase() +
        currentDate.format("MMMM YYYY").slice(1),
      value: currentDate.format("YYYY-MM"),
    });
    currentDate.add(1, "month");
  }

  return months.reverse(); // Mostrar primero los más recientes
};